import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, inp, btn, btnSecondary } from './AdminUsers';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

// New overview dashboard — surfaces, in one place, signals that previously
// only existed scattered across separate admin pages (User Directory's
// registration/MFA status, Active Sessions' live count, App Usage's
// per-app breakdown, Audit Log's login/failed-login/approval events).
// Backed by backend/src/routes/adminSsoStats.js — see that file's own
// header for exactly which Authentik/app-recorded events back each metric.
// Every number here is real; nothing is approximated beyond what each
// section's own label already says (e.g. "MFA Coverage" is explicitly a
// snapshot of configured accounts, not a per-login prompt rate — Authentik
// doesn't expose the latter).

// Same local date-math helpers as AdminAppUsage.jsx (not shared/exported —
// ~15 lines of pure date logic, cheap enough to duplicate rather than risk
// touching that already-shipped file to extract them).
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }

const PRESETS = [
  { key: 'today', label: 'Today', from: () => new Date() },
  // `to` defaults to "now" (see effectiveTo below) when a preset doesn't
  // set its own — Yesterday is the one preset here that genuinely needs
  // both ends pinned to the SAME single calendar day, not "yesterday
  // through now".
  { key: 'yesterday', label: 'Yesterday', from: () => new Date(Date.now() - 24 * 3600 * 1000), to: () => new Date(Date.now() - 24 * 3600 * 1000) },
  { key: 'week', label: 'This week', from: () => startOfWeek(new Date()) },
  { key: 'month', label: 'This month', from: () => startOfMonth(new Date()) },
  { key: '7d', label: 'Last 7 days', from: () => new Date(Date.now() - 7 * 24 * 3600 * 1000) },
  { key: '30d', label: 'Last 30 days', from: () => new Date(Date.now() - 30 * 24 * 3600 * 1000) },
  { key: 'custom', label: 'Custom' },
];

// BESCOM-blue-anchored semantic palette, consistent with the gradient on
// every existing primary button (`btn` from AdminUsers.jsx) and the
// status-badge colors AdminUsers.jsx's statusBadge()/DirectoryTable
// already use elsewhere in the admin section.
const C = {
  blue: '#009FE3', blueDark: '#0072B1',
  green: '#0F6E56', greenBg: '#E9F7EF',
  amber: '#C77700', amberBg: '#FFF6DA',
  orange: '#E67E22',
  red: '#B3261E', redBg: '#FDECEC',
  purple: '#534AB7',
  pink: '#E91E8C',
  teal: '#16A085',
  gray: '#8A8A86', grayBg: '#F1F0ED',
};

// Same mapping AdminActiveSessions.jsx keeps (not exported — duplicated
// here, same reasoning as this file's other small-helper duplications).
// Keep in sync with whatever slug each app's own tab-presence sender
// actually passes.
const APP_DISPLAY_NAMES = {
  'sso-portal': 'SSO Portal',
  'transfer': 'Transfer',
  'md-dashboard': 'MD Dashboard',
  'officers-directory': 'Contact Directory',
  'bicc-dashboard': 'BICC Dashboard',
  'coi-plan': 'Samvidhan',
  'unknown': 'Unknown app',
};
function appDisplayName(slug) {
  return APP_DISPLAY_NAMES[slug] || slug;
}

const sectionCardStyle = {
  background: '#fff', borderRadius: '16px', border: '1px solid #eee',
  boxShadow: '0 2px 10px rgba(20, 24, 33, 0.04)', padding: '20px 22px',
};

function SectionCard({ title, subtitle, action, children }) {
  return (
    <div style={sectionCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '12px', color: '#888', margin: '3px 0 0' }}>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// Elevated KPI tile — colored icon badge, big number, small label, and an
// optional trend arrow (vs. the prior 24h window) colored by whether the
// direction is actually good for THIS metric (more logins = fine either
// way, shown neutral; more failed logins = bad, shown red on an increase).
function KpiCard({ icon, label, value, sub, accent, accentBg, trendPct, trendBad, onClick }) {
  const hasTrend = typeof trendPct === 'number' && Number.isFinite(trendPct);
  const trendUp = hasTrend && trendPct > 0;
  const trendColor = !hasTrend || trendPct === 0 ? '#999' : (trendBad ? (trendUp ? C.red : C.green) : (trendUp ? C.green : C.gray));
  const clickable = typeof onClick === 'function';
  return (
    <div
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      className={clickable ? 'kpi-card-clickable' : undefined}
      style={{
        background: '#fff', borderRadius: '14px', border: '1px solid #eee',
        boxShadow: '0 2px 10px rgba(20, 24, 33, 0.04)', padding: '16px 18px',
        display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: '34px', height: '34px', borderRadius: '10px', background: accentBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0,
        }}>{icon}</div>
        <div style={{ fontSize: '12px', color: '#666', fontWeight: '600', lineHeight: 1.3, flex: 1 }}>{label}</div>
        {clickable && <span style={{ fontSize: '11px', color: '#bbb' }}>↗</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '26px', fontWeight: '800', color: accent, lineHeight: 1 }}>{value}</div>
        {hasTrend && (
          <span style={{ fontSize: '12px', fontWeight: '700', color: trendColor }}>
            {trendPct === 0 ? '—' : `${trendUp ? '▲' : '▼'} ${Math.abs(trendPct).toFixed(0)}%`}
          </span>
        )}
      </div>
      {sub && <div style={{ fontSize: '11px', color: '#999' }}>{sub}</div>}
    </div>
  );
}

// Same outer card shape as KpiCard (border/radius/shadow/padding) with
// gray placeholder bars instead of real content — so a loading KPI row
// still reads as "cards are here, filling in" rather than the section
// visibly disappearing and leaving bare "Loading…" text with no frame
// (confirmed live: that's what the very first page load showed, before
// the snapshot fetch resolved).
function KpiCardSkeleton() {
  return (
    <div style={{
      background: '#fff', borderRadius: '14px', border: '1px solid #eee',
      boxShadow: '0 2px 10px rgba(20, 24, 33, 0.04)', padding: '16px 18px',
      display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: '#f0f0f0' }} className="skeleton-pulse" />
        <div style={{ height: '10px', width: '70%', borderRadius: '4px', background: '#f0f0f0' }} className="skeleton-pulse" />
      </div>
      <div style={{ height: '22px', width: '45%', borderRadius: '4px', background: '#f0f0f0' }} className="skeleton-pulse" />
    </div>
  );
}

function pct(part, total) {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div style={{ background: '#1a1a1a', color: '#fff', padding: '6px 10px', borderRadius: '6px', fontSize: '12px' }}>
      {p.name}: <strong>{p.value}</strong>
    </div>
  );
};

function Donut({ data, total }) {
  const nonZero = data.filter((d) => d.value > 0);
  if (!nonZero.length) {
    return <div style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '30px 0' }}>No data yet</div>;
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
      <div style={{ width: '150px', height: '150px', position: 'relative', flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={nonZero} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={2} strokeWidth={0}>
              {nonZero.map((d) => <Cell key={d.name} fill={d.color} />)}
            </Pie>
            <Tooltip content={<DonutTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
        }}>
          <div style={{ fontSize: '20px', fontWeight: '800', color: '#1a1a1a' }}>{total}</div>
          <div style={{ fontSize: '10px', color: '#999' }}>total</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '140px' }}>
        {nonZero.map((d) => (
          <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '3px', background: d.color, flexShrink: 0 }} />
            <span style={{ color: '#444', flex: 1 }}>{d.name}</span>
            <span style={{ color: '#888', fontWeight: '600' }}>{d.value} ({pct(d.value, total)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const axisTick = { fontSize: 11, fill: '#999' };

// Recharts' default tooltip colors each series' name/value text in that
// series' OWN fill color against the dark tooltip background — fine for
// a bright color like the blue used elsewhere, but Approved's dark green
// (C.green, chosen to match the rest of this page's "healthy" color) was
// confirmed live to be nearly unreadable there. A swatch dot carries the
// series-identity color instead, so every value can stay uniformly
// legible (bold white) regardless of how dark its own series color is.
function ChartTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1a1a', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', minWidth: '120px' }}>
      {label != null && (
        <div style={{ color: '#aaa', fontWeight: 700, marginBottom: '4px' }}>
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      {payload.map((p) => (
        <div key={p.dataKey ?? p.name} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 0' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#ccc', flex: 1 }}>{p.name}</span>
          <span style={{ color: '#fff', fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

function fmtDay(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Recharts' "auto" Y-axis domain degenerates to a repeated "0,0,0,0" tick
// column on a SINGLE data point (CONFIRMED live — the "Today" preset,
// now the default, produces exactly one day of data) — an explicit
// numeric domain sidesteps that edge case entirely, for any point count.
function niceMax(values) {
  return Math.max(1, Math.ceil(Math.max(...values, 0) * 1.2));
}

function LoginsTrend({ data }) {
  if (!data.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="loginsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.blue} stopOpacity={0.35} />
            <stop offset="100%" stopColor={C.blue} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis domain={[0, niceMax(data.map((d) => d.count))]} allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={34} />
        <Tooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
        <Area type="monotone" dataKey="count" name="Logins" stroke={C.blue} strokeWidth={2} fill="url(#loginsFill)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// Distinct per-reason colors for the STACKED chart below — different from
// FAILURE_REASON_COLORS (defined further down, used for the drill-down
// modal's badges), which deliberately groups SMS/email/authenticator
// failures under one shared amber family since a badge differentiates by
// its own text label anyway. A stacked bar's segments have no label of
// their own, so sharing a color there would make two different reasons
// visually indistinguishable — each one gets its own color here instead.
const FAILURE_REASON_CHART_FIELDS = [
  { field: 'unknownUsername', name: 'Unknown username', color: C.gray },
  { field: 'wrongPassword', name: 'Wrong password', color: C.red },
  { field: 'wrongAuthenticator', name: 'Wrong authenticator code', color: C.orange },
  { field: 'wrongPasskey', name: 'Wrong passkey', color: C.purple },
  { field: 'wrongSms', name: 'Wrong SMS code', color: C.teal },
  { field: 'wrongEmail', name: 'Wrong email code', color: C.pink },
];

function FailedLoginsTrend({ data }) {
  if (!data.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis domain={[0, niceMax(data.map((d) => d.total))]} allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={34} />
        <Tooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
        <Legend wrapperStyle={{ fontSize: '11px' }} />
        {FAILURE_REASON_CHART_FIELDS.map(({ field, name, color }, i) => (
          <Bar
            key={field}
            dataKey={field}
            name={name}
            stackId="f"
            fill={color}
            radius={i === FAILURE_REASON_CHART_FIELDS.length - 1 ? [4, 4, 0, 0] : undefined}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function MfaEnrollmentsTrend({ data }) {
  if (!data.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis domain={[0, niceMax(data.map((d) => d.total))]} allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={34} />
        <Tooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Bar dataKey="totp" name="Authenticator" stackId="m" fill={C.blue} />
        <Bar dataKey="webauthn" name="Passkey" stackId="m" fill={C.purple} />
        <Bar dataKey="sms" name="SMS" stackId="m" fill={C.teal} />
        <Bar dataKey="code" name="Email/Code OTP" stackId="m" fill={C.pink} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ApprovalsTrend({ data }) {
  if (!data.length) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="date" tickFormatter={fmtDay} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis domain={[0, niceMax(data.flatMap((d) => [d.approved, d.rejected]))]} allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={34} />
        <Tooltip content={<ChartTooltip labelFormatter={fmtDay} />} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Bar dataKey="approved" name="Approved" fill={C.green} radius={[4, 4, 0, 0]} />
        <Bar dataKey="rejected" name="Rejected" fill={C.red} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// `apps` is the raw /api/admin/app-usage response array — kept whole
// (not stripped to just name/totalAccess) so distinctUsers and the real
// per-user breakdown are both available for the grouped bars below and
// for onAppClick's drill-down, without a second fetch.
function TopAppsChart({ apps, onAppClick }) {
  if (!apps.length) return <EmptyChart />;
  // Was capped at the top 5 — bumped to show every app with any access in
  // the period (the number of registered apps is small and bounded, so
  // this doesn't risk an unbounded-height chart the way e.g. a per-user
  // list would).
  const top = apps;
  const data = top.map((a) => ({ name: a.name, count: a.totalAccess, distinctUsers: a.distinctUsers }));
  const byName = Object.fromEntries(top.map((a) => [a.name, a]));
  const handleClick = (entry) => {
    const app = byName[entry?.name];
    if (app) onAppClick(app);
  };
  return (
    <ResponsiveContainer width="100%" height={Math.max(120, data.length * 42)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
        <XAxis type="number" domain={[0, niceMax(data.flatMap((d) => [d.count, d.distinctUsers]))]} allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={120} />
        <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0, 159, 227, 0.08)' }} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        <Bar dataKey="count" name="Accesses" fill={C.blue} radius={[0, 4, 4, 0]} cursor="pointer" onClick={(e) => handleClick(e.payload)} />
        <Bar dataKey="distinctUsers" name="Distinct Users" fill={C.purple} radius={[0, 4, 4, 0]} cursor="pointer" onClick={(e) => handleClick(e.payload)} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function EmptyChart() {
  return <div style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '40px 0' }}>No activity in this period</div>;
}

// Dims STALE data while a refetch is in flight (preset change, custom
// date edit, or the explicit Refresh button) instead of silently keeping
// the previous range's chart looking fully "current" with no cue at all
// that it's about to change — confirmed live, that silence was the whole
// complaint.
function FadeDuringLoad({ loading, children }) {
  return <div style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.15s ease' }}>{children}</div>;
}

function relativeTime(iso) {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// Shared overlay/card shell for the 3 KPI drill-down modals — same visual
// language as AdminAppUsage.jsx's own UserListModal (no modal library
// anywhere in this codebase, hand-rolled consistently).
function DrillDownModal({ title, subtitle, action, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: '14px', padding: '20px 24px', width: '480px', maxWidth: '100%', maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px', gap: '10px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#1a1a1a' }}>{title}</h2>
            {subtitle && <p style={{ fontSize: '12px', color: '#888', margin: '2px 0 0' }}>{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#888', padding: '0 4px', flexShrink: 0 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, marginTop: '10px' }}>{children}</div>
        {action && <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f0f0f0' }}>{action}</div>}
      </div>
    </div>
  );
}

const modalRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '9px 2px', borderBottom: '1px solid #f4f4f4', fontSize: '13px' };

// Category colors for the reasons adminSsoStats.js's describeFailureReason()
// can return — "Unknown username" is neutral (a typo, not necessarily this
// account's own problem), the rest flag the real account itself.
const FAILURE_REASON_COLORS = {
  'Unknown username': { color: C.gray, background: C.grayBg },
  'Wrong password': { color: C.red, background: C.redBg },
  'Wrong authenticator code': { color: C.orange, background: C.amberBg },
  'Wrong passkey': { color: C.purple, background: '#EFEDFB' },
  'Wrong SMS code': { color: C.orange, background: C.amberBg },
  'Wrong email code': { color: C.orange, background: C.amberBg },
};

function FailedLoginsModal({ events, rangeLabel, onClose }) {
  // Repeat-attempt counts within the window — the one genuinely new signal
  // a raw chronological list doesn't show at a glance (a real auth problem
  // or a brute-force attempt both show up as the same username recurring).
  const byUsername = new Map();
  for (const e of events) byUsername.set(e.username || 'unknown', (byUsername.get(e.username || 'unknown') || 0) + 1);
  const repeats = Array.from(byUsername.entries()).filter(([, n]) => n > 1).sort((a, b) => b[1] - a[1]);

  return (
    <DrillDownModal
      title={`Failed Logins (${rangeLabel})`}
      subtitle={`${events.length} attempt${events.length === 1 ? '' : 's'} shown`}
      onClose={onClose}
      action={<Link to="/admin/audit-log" style={{ fontSize: '12px', color: C.blueDark }}>View full Audit Log →</Link>}
    >
      {events.length === 0 ? (
        <EmptyChart />
      ) : (
        <>
          {repeats.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {repeats.map(([u, n]) => (
                <span key={u} style={{ fontSize: '11px', fontWeight: '700', color: C.red, background: C.redBg, padding: '3px 9px', borderRadius: '10px' }}>
                  {u} × {n}
                </span>
              ))}
            </div>
          )}
          {events.map((e, i) => {
            const reasonStyle = FAILURE_REASON_COLORS[e.reason] || { color: C.gray, background: C.grayBg };
            return (
              <div key={i} style={modalRowStyle}>
                <span>
                  <span style={{ color: '#2c2c2a', fontWeight: '600' }}>{e.username || 'Unknown username'}</span>
                  <span
                    style={{ marginLeft: '8px', fontSize: '11px', fontWeight: '700', color: reasonStyle.color, background: reasonStyle.background, padding: '2px 8px', borderRadius: '10px' }}
                  >
                    {e.reason}
                  </span>
                </span>
                <span style={{ color: '#888', flexShrink: 0 }} title={new Date(e.created).toLocaleString()}>{relativeTime(e.created)}</span>
              </div>
            );
          })}
        </>
      )}
    </DrillDownModal>
  );
}

function PendingApprovalsModal({ loading, error, list, onClose }) {
  return (
    <DrillDownModal
      title="Pending Approvals"
      subtitle={list ? `${list.length} account${list.length === 1 ? '' : 's'} awaiting sign-off` : undefined}
      onClose={onClose}
      action={<Link to="/admin/approvals" style={{ fontSize: '12px', color: C.blueDark }}>View full Approvals queue →</Link>}
    >
      {loading && <p style={{ textAlign: 'center', color: '#666', fontSize: '13px' }}>Loading…</p>}
      {error && <p style={{ textAlign: 'center', color: C.red, fontSize: '13px' }}>{error}</p>}
      {list && list.length === 0 && <EmptyChart />}
      {list && list.map((u) => (
        <Link key={u.pk} to={`/admin/users/${u.pk}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={{ ...modalRowStyle, cursor: 'pointer' }} className="admin-row-hover">
            <span>
              <span style={{ color: '#2c2c2a', fontWeight: '600' }}>{u.username}</span>
              {u.designation && <span style={{ color: '#888' }}> · {u.designation}</span>}
            </span>
            {u.reasons?.length > 0 && (
              <span style={{ fontSize: '11px', fontWeight: '700', color: C.amber, background: C.amberBg, padding: '2px 8px', borderRadius: '10px', flexShrink: 0 }}>
                {u.reasons[0]}
              </span>
            )}
          </div>
        </Link>
      ))}
    </DrillDownModal>
  );
}

// Collapses sessions that are functionally indistinguishable at a glance —
// same username AND the exact same set of active apps — down to one row,
// keeping whichever instance has real device info ("Chrome / Windows"
// beats "Unknown device", confirmed live these pairs are near-always the
// same real login split across a foreground-tab session and a background/
// gateway-sync one). The full, un-deduped picture — distinct session ids,
// for actually revoking one — is still one click away via "View full
// Active Sessions"; this modal is a glance-level summary, not a
// replacement for that page.
function dedupeSessions(sessions) {
  const byKey = new Map();
  const order = [];
  for (const s of sessions) {
    const appsKey = (s.apps || []).map((a) => a.app).sort().join(',');
    const key = `${s.username || ''}|${appsKey}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { ...s, mergedCount: 1 });
      order.push(key);
      continue;
    }
    existing.mergedCount += 1;
    if (!(existing.browser || existing.os) && (s.browser || s.os)) {
      existing.browser = s.browser;
      existing.os = s.os;
    }
    existing.lastIp = existing.lastIp || s.lastIp;
    // Earliest loginAt — "since" should reflect how long they've genuinely
    // been around, not just whichever duplicate session object is newest.
    if (s.loginAt && (!existing.loginAt || s.loginAt < existing.loginAt)) {
      existing.loginAt = s.loginAt;
    }
  }
  return order.map((k) => byKey.get(k));
}

function LiveActiveModal({ loading, error, list, onClose }) {
  const deduped = list ? dedupeSessions(list) : null;
  return (
    <DrillDownModal
      title="Live Active Now"
      subtitle={deduped ? `${deduped.length} shown${list.length !== deduped.length ? ` (${list.length} raw sessions)` : ''}` : undefined}
      onClose={onClose}
      action={<Link to="/admin/active-sessions" style={{ fontSize: '12px', color: C.blueDark }}>View full Active Sessions →</Link>}
    >
      {loading && <p style={{ textAlign: 'center', color: '#666', fontSize: '13px' }}>Loading…</p>}
      {error && <p style={{ textAlign: 'center', color: C.red, fontSize: '13px' }}>{error}</p>}
      {deduped && deduped.length === 0 && <EmptyChart />}
      {deduped && deduped.map((s) => (
        <Link key={s.sessionId} to={s.userPk ? `/admin/users/${s.userPk}` : '#'} style={{ textDecoration: 'none', color: 'inherit' }}>
          <div style={{ ...modalRowStyle, cursor: 'pointer', alignItems: 'flex-start' }} className="admin-row-hover">
            <span>
              <span style={{ color: '#2c2c2a', fontWeight: '600' }}>{s.username || 'Unknown'}</span>
              {s.mergedCount > 1 && (
                <span
                  title={`${s.mergedCount} separate Authentik sessions for this account, all active in the same app(s) — merged into one row here. "View full Active Sessions" shows each one separately.`}
                  style={{ marginLeft: '6px', fontSize: '10px', fontWeight: '700', color: C.gray, background: C.grayBg, padding: '1px 6px', borderRadius: '8px' }}
                >
                  {s.mergedCount} sessions
                </span>
              )}
              <div style={{ color: '#999', fontSize: '11px', marginTop: '2px' }}>
                {[s.browser, s.os].filter((v) => v && v !== 'Other').join(' / ') || 'Unknown device'}
                {s.lastIp && ` · ${s.lastIp}`}
              </div>
              {(s.apps || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                  {s.apps.map((a) => (
                    <span
                      key={a.app}
                      title={a.live ? 'Currently active' : 'No recent heartbeat'}
                      style={{
                        fontSize: '10px', fontWeight: '700', padding: '1px 7px', borderRadius: '8px',
                        color: a.live ? C.green : C.gray, background: a.live ? C.greenBg : C.grayBg,
                      }}
                    >
                      {appDisplayName(a.app)}
                    </span>
                  ))}
                </div>
              )}
            </span>
            <span style={{ color: '#888', fontSize: '12px', flexShrink: 0 }}>
              {s.loginAt ? `since ${relativeTime(new Date(s.loginAt).toISOString())}` : ''}
            </span>
          </div>
        </Link>
      ))}
    </DrillDownModal>
  );
}

function LoginsModal({ users, rangeLabel, onClose }) {
  return (
    <DrillDownModal
      title={`Logins (${rangeLabel})`}
      subtitle={`${users.length} distinct user${users.length === 1 ? '' : 's'}`}
      onClose={onClose}
      action={<Link to="/admin/audit-log" style={{ fontSize: '12px', color: C.blueDark }}>View full Audit Log →</Link>}
    >
      {users.length === 0 ? (
        <EmptyChart />
      ) : (
        users.map((u) => (
          <div key={u.username} style={modalRowStyle}>
            <span style={{ color: '#2c2c2a', fontWeight: '600' }}>{u.username}</span>
            <span style={{ color: '#888' }}>{u.count}×</span>
          </div>
        ))
      )}
    </DrillDownModal>
  );
}

// `app` is one row from the /api/admin/app-usage response (name,
// totalAccess, distinctUsers, users: [{username,count}]) — same shape
// AdminAppUsage.jsx's own UserListModal already renders, duplicated here
// rather than imported (that component isn't exported, same reasoning
// every other small duplication in this file already gives).
function AppUsersModal({ app, onClose }) {
  return (
    <DrillDownModal
      title={app.name}
      subtitle={`${app.totalAccess} access${app.totalAccess === 1 ? '' : 'es'} · ${app.distinctUsers} distinct user${app.distinctUsers === 1 ? '' : 's'}`}
      onClose={onClose}
      action={<Link to="/admin/app-usage" style={{ fontSize: '12px', color: C.blueDark }}>View full App Usage report →</Link>}
    >
      {(app.users || []).length === 0 ? (
        <EmptyChart />
      ) : (
        app.users.map((u) => (
          <div key={u.username} style={modalRowStyle}>
            <span style={{ color: '#2c2c2a', fontWeight: '600' }}>{u.username}</span>
            <span style={{ color: '#888' }}>{u.count}×</span>
          </div>
        ))
      )}
    </DrillDownModal>
  );
}

export default function AdminSsoStats() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) navigate('/dashboard');
  }, [user, authLoading, navigate]);

  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState(null);

  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(isoDate(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [customTo, setCustomTo] = useState(isoDate(new Date()));
  const activePreset = PRESETS.find((p) => p.key === preset);
  const effectiveFrom = preset === 'custom' ? customFrom : isoDate(activePreset.from());
  const effectiveTo = preset === 'custom' ? customTo : isoDate(activePreset.to ? activePreset.to() : new Date());
  const rangeLabel = preset === 'custom' ? 'custom range' : activePreset.label;

  const [trends, setTrends] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [trendsError, setTrendsError] = useState(null);

  const [topApps, setTopApps] = useState([]);
  const [topAppsLoading, setTopAppsLoading] = useState(true);

  // Drill-down modals for the 3 "interactive" KPI cards — failed logins'
  // own detail arrives with /trends (same selected range as the charts,
  // see adminSsoStats.js), the other two lazy-fetch an EXISTING endpoint
  // (adminApprovals.js's list, adminActiveSessions.js's own) on open
  // rather than duplicating that logic here, same reasoning the Top Apps
  // mini-chart already uses for /admin/app-usage.
  const [activeModal, setActiveModal] = useState(null); // null | 'logins' | 'failedLogins' | 'pendingApprovals' | 'liveActive' | 'appUsers'
  const [selectedApp, setSelectedApp] = useState(null); // the app row behind 'appUsers'
  const [approvalsList, setApprovalsList] = useState(null);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsError, setApprovalsError] = useState(null);
  const [liveSessions, setLiveSessions] = useState(null);
  const [liveSessionsLoading, setLiveSessionsLoading] = useState(false);
  const [liveSessionsError, setLiveSessionsError] = useState(null);

  useEffect(() => {
    if (activeModal === 'pendingApprovals' && approvalsList === null && !approvalsLoading) {
      setApprovalsLoading(true);
      setApprovalsError(null);
      fetch(`${API_BASE}/admin/approvals`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => setApprovalsList(data.results || []))
        .catch(() => setApprovalsError('Could not load the approvals queue.'))
        .finally(() => setApprovalsLoading(false));
    }
    if (activeModal === 'liveActive' && liveSessions === null && !liveSessionsLoading) {
      setLiveSessionsLoading(true);
      setLiveSessionsError(null);
      fetch(`${API_BASE}/admin/active-sessions`, { credentials: 'include' })
        .then((res) => res.json())
        .then((data) => setLiveSessions(data.sessions || []))
        .catch(() => setLiveSessionsError('Could not load active sessions.'))
        .finally(() => setLiveSessionsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);

  const fetchSnapshot = useCallback(async () => {
    setSnapshotError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/sso-stats/snapshot`, { credentials: 'include' });
      if (res.status === 401) { navigate('/', { state: { sessionExpired: true } }); return; }
      if (res.status === 403) { setSnapshotError('You are not authorized to view this dashboard.'); return; }
      const data = await res.json();
      if (!res.ok) { setSnapshotError(data.error || 'Could not load dashboard data.'); return; }
      setSnapshot(data);
    } catch (e) {
      setSnapshotError('Could not reach the identity service.');
    }
  }, [navigate]);

  const fetchTrends = useCallback(async () => {
    setTrendsLoading(true);
    setTrendsError(null);
    try {
      const params = new URLSearchParams({ from: effectiveFrom, to: effectiveTo });
      const res = await fetch(`${API_BASE}/admin/sso-stats/trends?${params}`, { credentials: 'include' });
      if (res.status === 401) { navigate('/', { state: { sessionExpired: true } }); return; }
      if (res.status === 403) { setTrendsError('You are not authorized to view this dashboard.'); return; }
      const data = await res.json();
      if (!res.ok) { setTrendsError(data.error || 'Could not load trend data.'); return; }
      setTrends(data);
    } catch (e) {
      setTrendsError('Could not reach the identity service.');
    } finally {
      setTrendsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, effectiveFrom, effectiveTo]);

  const fetchTopApps = useCallback(async () => {
    setTopAppsLoading(true);
    try {
      const params = new URLSearchParams({ from: effectiveFrom, to: effectiveTo });
      const res = await fetch(`${API_BASE}/admin/app-usage?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setTopApps(data.apps || []);
    } catch (e) {
      // non-critical mini-widget — silent fail, section just shows "no activity"
    } finally {
      setTopAppsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveFrom, effectiveTo]);

  useEffect(() => { fetchSnapshot(); }, [fetchSnapshot]);
  useEffect(() => { fetchTrends(); }, [fetchTrends]);
  useEffect(() => { fetchTopApps(); }, [fetchTopApps]);

  const s = snapshot;
  const pendingRegistration = s ? s.registrationStatusCounts.incomplete + s.registrationStatusCounts.profile_incomplete : 0;
  const registrationDonutData = s ? [
    { name: 'Active', value: s.registrationStatusCounts.active, color: C.green },
    { name: 'Registration Incomplete', value: s.registrationStatusCounts.incomplete, color: C.amber },
    { name: 'Profile Incomplete', value: s.registrationStatusCounts.profile_incomplete, color: C.orange },
    { name: 'Rejected', value: s.registrationStatusCounts.rejected, color: C.red },
    { name: 'Deactivated', value: s.registrationStatusCounts.deactivated, color: C.gray },
  ] : [];
  const mfaDonutData = s ? [
    { name: 'Authenticator App', value: s.mfa.counts['Authenticator App'], color: C.blue },
    { name: 'Passkey', value: s.mfa.counts['Passkey'], color: C.purple },
    { name: 'SMS Code', value: s.mfa.counts['SMS Code'], color: C.teal },
    { name: 'Email/Code OTP', value: s.mfa.counts['Email/Code OTP'], color: C.pink },
    { name: 'None', value: s.mfa.counts['None'], color: C.red },
  ] : [];

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
        <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
          <AdminSidebar active="sso-stats" />
          <div style={{ ...cardScroll, background: '#F8FAFC' }}>
            <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
              SSO Statistics
            </h1>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              A live overview of logins, registrations, MFA adoption, and app usage across the whole SSO ecosystem.
            </p>

            {snapshotError && (
              <div style={{ background: C.redBg, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                {snapshotError}
              </div>
            )}

            {/* KPI bar — "right now" snapshot data (no historical daily
                snapshots are stored, so these can't be recomputed for a
                past date — unlike Logins/Failed Logins just below the
                date-range picker, which genuinely are just counts over a
                window and do follow it). */}
            <p style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>
              Right now
            </p>
            {s ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                <KpiCard icon="👥" label="Total Users" value={s.totalUsers} accent="#1a1a1a" accentBg={C.grayBg} />
                <KpiCard icon="✅" label="Active Accounts" value={s.registrationStatusCounts.active} sub={`${pct(s.registrationStatusCounts.active, s.totalUsers)}% of total`} accent={C.green} accentBg={C.greenBg} />
                <KpiCard icon="⏳" label="Pending Registration" value={pendingRegistration} accent={C.amber} accentBg={C.amberBg} />
                <KpiCard icon="🗂️" label="Pending Approvals" value={s.pendingApprovals} accent={C.orange} accentBg={C.amberBg} onClick={() => setActiveModal('pendingApprovals')} />
                <KpiCard icon="🔐" label="MFA Coverage" value={`${pct(s.mfa.configured, s.mfa.total)}%`} sub={`${s.mfa.configured} / ${s.mfa.total} accounts`} accent={C.purple} accentBg="#EFEDFB" />
                <KpiCard icon="🟢" label="Live Active Now" value={s.liveActiveUsers} sub={`${s.liveActiveSessions} session${s.liveActiveSessions === 1 ? '' : 's'}`} accent={C.green} accentBg={C.greenBg} onClick={() => setActiveModal('liveActive')} />
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '24px' }}>
                {Array.from({ length: 6 }).map((_, i) => <KpiCardSkeleton key={i} />)}
              </div>
            )}

            {/* Snapshot breakdowns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              {s ? (
                <>
                  <SectionCard title="Registration Status" subtitle="Every account in the directory, right now">
                    <Donut data={registrationDonutData} total={s.totalUsers} />
                  </SectionCard>
                  <SectionCard title="MFA Method Mix" subtitle="How accounts with a configured factor are signing in">
                    <Donut data={mfaDonutData} total={s.mfa.total} />
                  </SectionCard>
                </>
              ) : (
                <>
                  <SectionCard title="Registration Status" subtitle="Every account in the directory, right now">
                    <p style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '30px 0' }}>Loading…</p>
                  </SectionCard>
                  <SectionCard title="MFA Method Mix" subtitle="How accounts with a configured factor are signing in">
                    <p style={{ textAlign: 'center', color: '#999', fontSize: '13px', padding: '30px 0' }}>Loading…</p>
                  </SectionCard>
                </>
              )}
            </div>

            {/* Date range controls for everything below, including the
                Logins/Failed Logins KPI cards right after it — these two
                (unlike the "Right now" grid above) genuinely are just
                counts over a window, so they follow this same picker. */}
            <p style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              Trends for: {rangeLabel}
              {/* Fires on EVERY trends refetch — a preset click, a custom
                  date change, or the explicit Refresh button below — not
                  just the button's own disabled/label state (confirmed
                  live: switching presets showed no loading indication at
                  all before this, the charts just silently kept the
                  PREVIOUS range's data up until the new one arrived). */}
              {(trendsLoading || topAppsLoading) && (
                <span style={{ color: C.blue, fontWeight: '700', textTransform: 'none', letterSpacing: 'normal' }}>· updating…</span>
              )}
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              {PRESETS.map((p) => (
                <button
                  key={p.key} type="button" onClick={() => setPreset(p.key)}
                  style={{
                    ...btn, width: 'auto', padding: '7px 16px', fontSize: '13px',
                    background: preset === p.key ? undefined : '#fff',
                    color: preset === p.key ? undefined : '#333',
                    border: preset === p.key ? 'none' : '1px solid #ddd',
                  }}
                >
                  {p.label}
                </button>
              ))}
              {preset === 'custom' && (
                <>
                  <label style={{ fontSize: '13px', color: '#666' }}>
                    From <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)} style={{ ...inp, width: 'auto', padding: '6px 10px' }} />
                  </label>
                  <label style={{ fontSize: '13px', color: '#666' }}>
                    To <input type="date" value={customTo} min={customFrom} max={isoDate(new Date())} onChange={(e) => setCustomTo(e.target.value)} style={{ ...inp, width: 'auto', padding: '6px 10px' }} />
                  </label>
                </>
              )}
              {/* Re-fetches only this section (trends + top apps), not the
                  "Right now" KPIs above — picking the same preset again
                  doesn't re-trigger a fetch (React state doesn't change),
                  so this is the only way to pull fresh numbers for the
                  currently-selected range without switching away and back. */}
              <button
                type="button"
                onClick={() => { fetchTrends(); fetchTopApps(); }}
                disabled={trendsLoading || topAppsLoading}
                style={{
                  ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '13px', marginTop: 0,
                  opacity: (trendsLoading || topAppsLoading) ? 0.6 : 1,
                  cursor: (trendsLoading || topAppsLoading) ? 'not-allowed' : 'pointer',
                }}
              >
                {(trendsLoading || topAppsLoading) ? 'Refreshing…' : '↻ Refresh'}
              </button>
            </div>

            {trendsError && (
              <div style={{ background: C.redBg, color: C.red, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                {trendsError}
              </div>
            )}

            {trends && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                <KpiCard
                  icon="🔑" label={`Logins (${rangeLabel})`} value={trends.loginsTotal}
                  sub={`${trends.distinctLoginUsers} distinct user${trends.distinctLoginUsers === 1 ? '' : 's'}`}
                  accent={C.blue} accentBg="#E6F6FE"
                  onClick={() => setActiveModal('logins')}
                />
                <KpiCard
                  icon="⚠️" label={`Failed Logins (${rangeLabel})`} value={trends.failedLoginsTotal}
                  accent={C.red} accentBg={C.redBg}
                  onClick={() => setActiveModal('failedLogins')}
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <SectionCard
                title="Logins per day"
                subtitle="Real authentications — distinct from per-app access events below"
                action={<Link to="/admin/audit-log" style={{ fontSize: '12px', color: C.blueDark }}>View Audit Log →</Link>}
              >
                {trendsLoading && !trends ? <EmptyChart /> : <FadeDuringLoad loading={trendsLoading}><LoginsTrend data={trends?.logins || []} /></FadeDuringLoad>}
              </SectionCard>
              <SectionCard title="Failed logins per day" subtitle="Bars in red mark a day spiking well above the period's own average">
                {trendsLoading && !trends ? <EmptyChart /> : <FadeDuringLoad loading={trendsLoading}><FailedLoginsTrend data={trends?.failedLogins || []} /></FadeDuringLoad>}
              </SectionCard>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <SectionCard title="MFA enrollments per day" subtitle="New factors set up, by type">
                {trendsLoading && !trends ? <EmptyChart /> : <FadeDuringLoad loading={trendsLoading}><MfaEnrollmentsTrend data={trends?.mfaEnrollments || []} /></FadeDuringLoad>}
              </SectionCard>
              <SectionCard title="Approvals per day" subtitle="User-creation approval decisions">
                {trendsLoading && !trends ? <EmptyChart /> : <FadeDuringLoad loading={trendsLoading}><ApprovalsTrend data={trends?.approvals || []} /></FadeDuringLoad>}
              </SectionCard>
            </div>

            <SectionCard
              title="Top Apps by Access"
              subtitle="For the selected period"
              action={<Link to="/admin/app-usage" style={{ fontSize: '12px', color: C.blueDark }}>View full App Usage report →</Link>}
            >
              {topAppsLoading && !topApps.length ? <EmptyChart /> : (
                <FadeDuringLoad loading={topAppsLoading}>
                  <TopAppsChart apps={topApps} onAppClick={(app) => { setSelectedApp(app); setActiveModal('appUsers'); }} />
                </FadeDuringLoad>
              )}
            </SectionCard>
          </div>
        </div>
      </div>

      {activeModal === 'logins' && trends && (
        <LoginsModal users={trends.loginUsers || []} rangeLabel={rangeLabel} onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'failedLogins' && trends && (
        <FailedLoginsModal events={trends.failedLoginsEvents || []} rangeLabel={rangeLabel} onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'pendingApprovals' && (
        <PendingApprovalsModal loading={approvalsLoading} error={approvalsError} list={approvalsList} onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'liveActive' && (
        <LiveActiveModal loading={liveSessionsLoading} error={liveSessionsError} list={liveSessions} onClose={() => setActiveModal(null)} />
      )}
      {activeModal === 'appUsers' && selectedApp && (
        <AppUsersModal app={selectedApp} onClose={() => setActiveModal(null)} />
      )}
    </>
  );
}
